import React, { useState, useEffect } from "react";
import { API } from "../../App";
import { toast } from "sonner";
import axios from "axios";
import {
  Calendar, ChevronLeft, ChevronRight, MapPin, Clock, Users,
  Briefcase, AlertCircle
} from "lucide-react";

const PRIORITY_COLORS = {
  low: "border-l-gray-500",
  normal: "border-l-blue-500",
  high: "border-l-orange-500",
  urgent: "border-l-red-500"
};

const STATUS_COLORS = {
  scheduled: "bg-blue-500/20 text-blue-400",
  in_progress: "bg-yellow-500/20 text-yellow-400",
  pending: "bg-yellow-500/20 text-yellow-400",
  confirmed: "bg-green-500/20 text-green-400"
};

const ScheduleTab = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("week"); // week, month

  useEffect(() => { fetchSchedule(); }, [currentDate]);

  const fetchSchedule = async () => {
    try {
      const res = await axios.get(`${API}/schedule`, { withCredentials: true });
      setEvents(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Get dates for current week
  const getWeekDates = () => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay()); // Sunday
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  // Get dates for current month
  const getMonthDates = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const dates = [];
    let d = new Date(startDate);
    while (d <= lastDay || d.getDay() !== 0) {
      dates.push(new Date(d));
      d.setDate(d.getDate() + 1);
      if (dates.length >= 42) break;
    }
    return dates;
  };

  const formatDateKey = (d) => d.toISOString().split("T")[0];
  const isToday = (d) => formatDateKey(d) === formatDateKey(new Date());
  const isCurrentMonth = (d) => d.getMonth() === currentDate.getMonth();

  const getEventsForDate = (dateStr) => {
    return events.filter(e => e.date === dateStr);
  };

  const navigateWeek = (dir) => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + (dir * 7));
    setCurrentDate(d);
  };

  const navigateMonth = (dir) => {
    const d = new Date(currentDate);
    d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const navigate = (dir) => viewMode === "week" ? navigateWeek(dir) : navigateMonth(dir);

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const dates = viewMode === "week" ? getWeekDates() : getMonthDates();

  return (
    <div className="space-y-4" data-testid="schedule-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate(-1)} className="p-2 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-[#A0AAB2] hover:text-white">
            <ChevronLeft size={18} />
          </button>
          <h2 className="text-lg font-semibold text-white min-w-[200px] text-center">
            {viewMode === "week" ? (
              `${dates[0]?.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${dates[6]?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
            ) : (
              `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
            )}
          </h2>
          <button onClick={() => navigate(1)} className="p-2 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-[#A0AAB2] hover:text-white">
            <ChevronRight size={18} />
          </button>
          <button onClick={() => setCurrentDate(new Date())} className="px-3 py-2 bg-[#FF6B35]/20 text-[#FF6B35] rounded-lg text-sm font-medium">
            Today
          </button>
        </div>
        <div className="flex gap-2">
          {["week", "month"].map(v => (
            <button key={v} onClick={() => setViewMode(v)} data-testid={`schedule-view-${v}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${viewMode === v ? "bg-[#FF6B35] text-white" : "bg-[#1F2027] text-[#A0AAB2] border border-[#2A2B32]"}`}>
              {v}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FF6B35]"></div>
        </div>
      ) : (
        <>
          {/* Week View */}
          {viewMode === "week" && (
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl overflow-hidden">
              <div className="grid grid-cols-7 border-b border-[#2A2B32]">
                {dates.map((d, i) => (
                  <div key={i} className={`p-3 text-center border-r border-[#2A2B32] last:border-r-0 ${isToday(d) ? "bg-[#FF6B35]/10" : ""}`}>
                    <p className="text-[#A0AAB2] text-xs">{dayNames[d.getDay()]}</p>
                    <p className={`text-lg font-bold ${isToday(d) ? "text-[#FF6B35]" : "text-white"}`}>{d.getDate()}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 min-h-[300px]">
                {dates.map((d, i) => {
                  const dayEvents = getEventsForDate(formatDateKey(d));
                  return (
                    <div key={i} className={`p-2 border-r border-[#2A2B32] last:border-r-0 ${isToday(d) ? "bg-[#FF6B35]/5" : ""}`}>
                      {dayEvents.map(ev => (
                        <div key={ev.id} className={`mb-1 p-2 rounded-lg bg-[#1F2027] border-l-2 ${PRIORITY_COLORS[ev.priority] || "border-l-blue-500"} cursor-pointer hover:bg-[#2A2B32] transition-colors`}
                          title={`${ev.title}\n${ev.client_name}\n${ev.time}`}>
                          <p className="text-white text-xs font-medium truncate">{ev.title}</p>
                          {ev.time && <p className="text-[#A0AAB2] text-[10px]">{ev.time}</p>}
                          {ev.assigned_employees?.length > 0 && (
                            <p className="text-[#FF6B35] text-[10px] truncate">{ev.assigned_employees.join(", ")}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Month View */}
          {viewMode === "month" && (
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl overflow-hidden">
              <div className="grid grid-cols-7 border-b border-[#2A2B32]">
                {dayNames.map(d => (
                  <div key={d} className="p-2 text-center">
                    <p className="text-[#A0AAB2] text-xs font-medium">{d}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {dates.map((d, i) => {
                  const dayEvents = getEventsForDate(formatDateKey(d));
                  return (
                    <div key={i} className={`min-h-[80px] p-1 border-r border-b border-[#2A2B32] ${!isCurrentMonth(d) ? "opacity-30" : ""} ${isToday(d) ? "bg-[#FF6B35]/5" : ""}`}>
                      <p className={`text-xs mb-1 ${isToday(d) ? "text-[#FF6B35] font-bold" : "text-[#A0AAB2]"}`}>{d.getDate()}</p>
                      {dayEvents.slice(0, 2).map(ev => (
                        <div key={ev.id} className={`mb-0.5 px-1.5 py-0.5 rounded text-[10px] ${ev.type === "job" ? "bg-[#FF6B35]/20 text-[#FF6B35]" : "bg-blue-500/20 text-blue-400"} truncate`}>
                          {ev.title}
                        </div>
                      ))}
                      {dayEvents.length > 2 && <p className="text-[#A0AAB2] text-[10px] px-1">+{dayEvents.length - 2} more</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Upcoming List */}
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
            <h3 className="text-base font-semibold text-white mb-4">Upcoming Schedule ({events.length})</h3>
            {events.length === 0 ? (
              <p className="text-[#A0AAB2] text-center py-6">No upcoming events. Create jobs and schedule them to see them here.</p>
            ) : (
              <div className="space-y-2">
                {events.map(ev => (
                  <div key={ev.id} className={`bg-[#1F2027] border-l-2 ${PRIORITY_COLORS[ev.priority] || "border-l-blue-500"} border border-[#2A2B32] rounded-lg p-3`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${ev.type === "job" ? "bg-[#FF6B35]/20 text-[#FF6B35]" : "bg-blue-500/20 text-blue-400"}`}>
                            {ev.type}
                          </span>
                          <h4 className="text-white font-medium text-sm">{ev.title}</h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[ev.status] || "bg-gray-500/20 text-gray-400"}`}>
                            {ev.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-[#A0AAB2] text-xs">
                          {ev.client_name && <span>{ev.client_name}</span>}
                          {ev.date && <span className="flex items-center space-x-1"><Clock size={10} /><span>{ev.date} {ev.time}</span></span>}
                          {ev.address && <span className="flex items-center space-x-1"><MapPin size={10} /><span>{ev.address}</span></span>}
                          {ev.assigned_employees?.length > 0 && (
                            <span className="flex items-center space-x-1 text-[#FF6B35]"><Users size={10} /><span>{ev.assigned_employees.join(", ")}</span></span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ScheduleTab;
